"use client";

import { useEffect, useState } from "react";
import type { OwletInitialContext } from "./definitions";

const POLL_MS = 250;
const PORTAL_CONTEXT_EVENT = "portalInitialContextChanged";

/** Decode a JWT payload (no signature verification — the bridge id_token
 *  is already trusted) and return the `user_email` claim if present. */
function readUserEmailFromJwt(jwt: string): string | null {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    return typeof payload.user_email === "string" && payload.user_email
      ? payload.user_email
      : null;
  } catch {
    return null;
  }
}

/** Read `user_email` from the bridge id_token JWT on `window.portalInitialContext`.
 *  Refresh-issued tokens drop the claim, so this only reflects the *initial*
 *  injection — callers should treat it as a best-effort enhancement.
 *
 *  `window.portalInitialContext` is injected by the native shell and may not
 *  be present on first render, so we poll briefly until it shows up (and
 *  listen for the SDK's change event for good measure). */
export function useTrustedUserEmail(): string | null {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const tryCapture = (): boolean => {
      const ctx = (window as { portalInitialContext?: OwletInitialContext })
        .portalInitialContext;
      const idToken = ctx?.account?.custom?.idToken;
      if (!idToken) return false;
      const fresh = readUserEmailFromJwt(idToken);
      if (!fresh) return false;
      setEmail((prev) => (prev === fresh ? prev : fresh));
      return true;
    };

    if (tryCapture()) return;
    const onChange = () => {
      if (tryCapture()) cleanup();
    };
    const intervalId = setInterval(() => {
      if (tryCapture()) cleanup();
    }, POLL_MS);
    window.addEventListener(PORTAL_CONTEXT_EVENT, onChange);
    const cleanup = () => {
      clearInterval(intervalId);
      window.removeEventListener(PORTAL_CONTEXT_EVENT, onChange);
    };
    return cleanup;
  }, []);

  return email;
}
